import { lazy } from 'react'
import { Navigate, type RouteObject } from 'react-router'

import { hasOnlinePaymentSectionEnabled } from '@/resources/services/permission/permissions'

import AuthenticatedRoute from './authenticated'
import SplitControl from '@/components/business/SplitControl'
import SplitHandlerScheduledOrders from '@/pages/ScheduledOrders/components/SplitHandler'
import SplitHandlerOnSales from '@/pages/OnSale/components/SplitHandler'
import OrderHistoryStoreProvider from '@/contexts/orderHistory'
import InventoryStoreProvider from '@/contexts/inventory'
import { AutomaticPrintProvider } from '@/contexts/automaticPrint'
import ActiveOrdersNanodropProvider from '@/contexts/activeOrdersNanodrop'
import { OrdersProvider } from '@/contexts/orders'
import { CouponProvider } from '@/contexts/coupon/useCouponContext'

const DatesAvailability = lazy(() => import('../pages/DatesAvailability'))
const Management = lazy(() => import('../pages/Management'))
const Performance = lazy(() => import('../pages/Performance'))
const TenMinutesReleaseRate = lazy(
  () => import('../pages/Performance/subPages/TenMinutesReleaseRate')
)
const Inventory = lazy(() => import('../pages/Inventory'))
const SearchedProducts = lazy(
  () => import('../pages/Inventory/subpages/SearchedProducts')
)
const Orders = lazy(() => import('../pages/Orders'))
const Order = lazy(() => import('../pages/Order'))
const OrdersHistory = lazy(() => import('../pages/OrdersHistory'))
const FleetManagement = lazy(() => import('../pages/FleetManagement'))
const MyFleet = lazy(() => import('../pages/FleetManagement/subpages/MyFleet'))
const NoDeliverymanLinked = lazy(
  () => import('../pages/FleetManagement/subpages/NoDeliverymanLinked')
)
const MyAccount = lazy(() => import('../pages/MyAccount'))
const DeliverymanApproval = lazy(
  () => import('../pages/MyAccount/subpages/DeliverymanApproval')
)
const DeliverymanManager = lazy(
  () => import('../pages/MyAccount/subpages/DeliverymanManager')
)
const PartnerBankDetails = lazy(
  () => import('../pages/MyAccount/subpages/PartnerBankDetails')
)
const EditPocInfo = lazy(
  () => import('../pages/MyAccount/subpages/EditPocInfo')
)
const OnSale = lazy(() => import('../pages/OnSale'))
const OnlinePayment = lazy(() => import('../pages/OnlinePayment'))
const NotFound = lazy(() => import('../pages/NotFound'))
const Maintenance = lazy(() => import('../pages/Maintenance'))
const OrdersMap = lazy(() => import('../pages/OrdersMap'))
const DynamicRadius = lazy(() => import('../pages/DynamicRadius'))
const FinancialReport = lazy(() => import('../pages/FinancialReport'))
const ScheduledOrders = lazy(() => import('../pages/ScheduledOrders'))
const Home = lazy(() => import('../pages/Home'))
const Coupon = lazy(() => import('../pages/Coupon'))
const Coupons = lazy(() => import('../pages/Coupons'))
const RestitutionDetails = lazy(() => import('../pages/RestitutionDetails'))
const OrdersNanoDrop = lazy(() => import('../pages/NanoDrop'))
const NanoDropOrder = lazy(() => import('../pages/NanoDropOrder'))

const OnlinePaymentRoute = () => {
  if (hasOnlinePaymentSectionEnabled()) {
    return (
      <AuthenticatedRoute>
        <OnlinePayment />
      </AuthenticatedRoute>
    )
  }
  return <Navigate to="/poc-orders" />
}

export const privateRoutes: RouteObject[] = [
  {
    path: 'poc-orders',
    element: (
      <AuthenticatedRoute>
        <InventoryStoreProvider>
          <OrdersProvider>
            <AutomaticPrintProvider>
              <Orders />
            </AutomaticPrintProvider>
          </OrdersProvider>
        </InventoryStoreProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'coupons',
    element: (
      <AuthenticatedRoute>
        <Coupons />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'history',
    element: (
      <AuthenticatedRoute>
        <OrderHistoryStoreProvider>
          <OrdersHistory />
        </OrderHistoryStoreProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'order/:id',
    element: (
      <AuthenticatedRoute>
        <InventoryStoreProvider>
          <Order />
        </InventoryStoreProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'create-coupon',
    element: (
      <AuthenticatedRoute>
        <CouponProvider>
          <Coupon />
        </CouponProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'coupon/:id',
    element: (
      <AuthenticatedRoute>
        <CouponProvider>
          <Coupon />
        </CouponProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'inventory',
    element: <AuthenticatedRoute />,
    children: [
      {
        index: true,
        element: (
          <InventoryStoreProvider>
            <Inventory />
          </InventoryStoreProvider>
        ),
      },
      {
        path: 'searched-products',
        element: <SearchedProducts />,
      },
    ],
  },
  {
    path: 'fleet-management',
    element: <AuthenticatedRoute />,
    children: [
      {
        index: true,
        element: <FleetManagement />,
      },
      {
        path: 'my-fleet',
        element: <MyFleet />,
      },
      {
        path: 'no-deliveryman-linked',
        element: <NoDeliverymanLinked />,
      },
    ],
  },
  {
    path: 'my-account',
    element: <AuthenticatedRoute />,
    children: [
      {
        index: true,
        element: <MyAccount />,
      },
      {
        path: 'deliveryman-approval',
        element: <DeliverymanApproval />,
      },
      {
        path: 'deliveryman-manager',
        element: <DeliverymanManager />,
      },
      {
        path: 'partner-bank-details',
        element: <PartnerBankDetails />,
      },
      {
        path: 'edit-poc-info',
        element: <EditPocInfo />,
      },
    ],
  },
  {
    path: 'performance',
    element: <AuthenticatedRoute />,
    children: [
      {
        index: true,
        element: <Performance />,
      },
      {
        path: 'ten-minutes-delivery-release-rate',
        element: <TenMinutesReleaseRate />,
      },
    ],
  },
  {
    path: 'management',
    element: (
      <AuthenticatedRoute>
        <Management />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'dates-availability',
    element: (
      <AuthenticatedRoute>
        <DatesAvailability />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'orders-map',
    element: (
      <AuthenticatedRoute>
        <OrdersMap />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'on-sale',
    element: (
      <AuthenticatedRoute>
        <InventoryStoreProvider>
          <SplitHandlerOnSales>
            <OnSale />
          </SplitHandlerOnSales>
        </InventoryStoreProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'online-payment',
    element: <OnlinePaymentRoute />,
  },
  {
    path: 'maintenance',
    element: (
      <AuthenticatedRoute>
        <Maintenance />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'dynamic-radius',
    element: (
      <AuthenticatedRoute>
        <DynamicRadius />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'financial-report',
    element: (
      <AuthenticatedRoute>
        <FinancialReport />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'home',
    element: (
      <AuthenticatedRoute>
        <SplitControl
          splitName="new_home_page"
          treatmentOff={
            <Navigate to="/poc-orders" state={{ previousPage: '/login' }} />
          }
        >
          <Home />
        </SplitControl>
      </AuthenticatedRoute>
    ),
  },
  {
    path: '/',
    element: <Navigate to="/home" />,
  },
  {
    path: 'poc-orders-scheduled',
    element: (
      <AuthenticatedRoute>
        <SplitHandlerScheduledOrders>
          <ScheduledOrders />
        </SplitHandlerScheduledOrders>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'restitution-details/:yearWeek',
    element: (
      <AuthenticatedRoute>
        <RestitutionDetails />
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'nano-drop',
    element: (
      <AuthenticatedRoute>
        <SplitControl
          splitName="seller_nanodrop_enable"
          treatmentOff={<Navigate to="/poc-orders" replace />}
        >
          <ActiveOrdersNanodropProvider>
            <AutomaticPrintProvider>
              <OrdersNanoDrop />
            </AutomaticPrintProvider>
          </ActiveOrdersNanodropProvider>
        </SplitControl>
      </AuthenticatedRoute>
    ),
  },
  {
    path: 'nano-drop/order/:orderNumber',
    element: (
      <AuthenticatedRoute>
        <SplitControl
          splitName="seller_nanodrop_enable"
          treatmentOff={<Navigate to="/poc-orders" replace />}
        >
          <ActiveOrdersNanodropProvider>
            <AutomaticPrintProvider>
              <NanoDropOrder />
            </AutomaticPrintProvider>
          </ActiveOrdersNanodropProvider>
        </SplitControl>
      </AuthenticatedRoute>
    ),
  },
  {
    path: '*',
    element: (
      <AuthenticatedRoute>
        <NotFound />
      </AuthenticatedRoute>
    ),
  },
]
